import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import { Payment, PaymentFormData, CreatePaymentRequest } from "../types/payments";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  AlertCircle,
  X,
  Loader2,
  Upload,
  DollarSign,
  Calendar,
  FileText,
  Check,
} from "lucide-react";

// Helper to format date for input field (YYYY-MM-DD)
const formatDateForInput = (dateStr: string): string => {
  try {
    // Handle DD/MM/YYYY format (most common in documents)
    const parts = dateStr.replace(/\//g, "-").split("-");
    
    if (parts.length === 3) {
      const day = parts[0].padStart(2, "0");
      const month = parts[1].padStart(2, "0");
      const year = parts[2];

      // Check if year is 4 digits (YYYY) - then it's DD-MM-YYYY format
      if (year.length === 4 && !isNaN(parseInt(day)) && !isNaN(parseInt(month)) && !isNaN(parseInt(year))) {
        // DD-MM-YYYY or DD-MM-YYYY format
        return `${year}-${month}-${day}`;
      } else if (day.length === 4) {
        // YYYY-MM-DD format
        return `${day}-${month}-${parts[2].padStart(2, "0")}`;
      }
    }
  } catch (e) {
    console.error("Error formatting date:", e);
  }
  
  return new Date().toISOString().split("T")[0];
};

export const PaymentsPage = () => {
  const [paymentRecords, setPaymentRecords] = useState<Payment[]>([]);
  const [allPaymentRecords, setAllPaymentRecords] = useState<Payment[]>([]);
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    paymentId: string;
    documentNumber: string;
  } | null>(null);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [pdfExtractedData, setPdfExtractedData] = useState<CreatePaymentRequest[]>([]);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const authToken = useSelector((state: any) => state.loginData?.authToken);
  const id = useSelector((state: any) => state.loginData?.id);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const todayDate = getTodayDate();

  const [formData, setFormData] = useState<PaymentFormData>({
    documentNumber: "",
    amount: "",
    date: todayDate,
    netAmount: "",
    invoiceNo: "",
  });

  // Fetch all Payments records for search
  const fetchAllPaymentsForSearch = async () => {
    if (!authToken || !id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/payments?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setAllPaymentRecords(response.data.payments || []);
      setTotalPayments(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching payments:", error);
      alert("Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  };

  // Get paginated records
  const getPaginatedRecords = (recordsToDisplay: Payment[]) => {
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  useEffect(() => {
    fetchAllPaymentsForSearch();
  }, [authToken, id]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.documentNumber.trim()) newErrors.documentNumber = "Document Number is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    if (isNaN(Number(formData.amount))) newErrors.amount = "Amount must be a number";
    if (!formData.date) newErrors.date = "Date is required";
    if (!formData.netAmount) newErrors.netAmount = "Net Amount is required";
    if (isNaN(Number(formData.netAmount))) newErrors.netAmount = "Net Amount must be a number";
    if (!formData.invoiceNo.trim()) newErrors.invoiceNo = "Invoice No is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsCreating(true);
    try {
      const payload = {
        documentNumber: formData.documentNumber,
        amount: Number(formData.amount),
        date: formData.date,
        netAmount: Number(formData.netAmount),
        invoiceNo: formData.invoiceNo,
      };

      if (editingPaymentId) {
        await axios.put(
          `${API_BASE_URL}account/${id}/payments/${editingPaymentId}/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("Payment updated successfully");
        setEditingPaymentId(null);
      } else {
        await axios.post(
          `${API_BASE_URL}account/${id}/payments/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("Payment created successfully");
      }

      setFormData({
        documentNumber: "",
        amount: "",
        date: todayDate,
        netAmount: "",
        invoiceNo: "",
      });

      setIsDrawerOpen(false);
      await fetchAllPaymentsForSearch();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Error creating/updating payment:", error);
      alert(error.response?.data?.message || "Failed to save payment");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = (payment: Payment) => {
    setFormData({
      documentNumber: payment.documentNumber,
      amount: payment.amount.toString(),
      date: payment.date,
      netAmount: payment.netAmount.toString(),
      invoiceNo: payment.invoiceNo,
    });
    setEditingPaymentId(payment.paymentId);
    setIsDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/payments/${deleteConfirm.paymentId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSuccessMessage("Payment deleted successfully");
      setDeleteConfirm(null);
      await fetchAllPaymentsForSearch();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting payment:", error);
      alert("Failed to delete payment");
    }
  };

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate it's a PDF
    if (!file.name.endsWith(".pdf")) {
      alert("Please upload a valid PDF file");
      return;
    }

    setUploadingPdf(true);
    try {
      // Create FormData for multipart upload
      const formDataObj = new FormData();
      formDataObj.append("pdf", file);

      // Send PDF to backend for extraction using ledongthuc/pdf library
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/payments/extract-from-pdf`,
        formDataObj,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const extractedPayments = response.data.payments || [];

      if (extractedPayments.length === 0) {
        setPdfExtractedData([
          {
            documentNumber: "",
            amount: 0,
            date: todayDate,
            netAmount: 0,
            invoiceNo: "",
          },
        ]);
        alert("No payment data found in PDF. Please fill in the form manually.");
      } else {
        setPdfExtractedData(extractedPayments);
      }

      setShowPdfPreview(true);
    } catch (error: any) {
      console.error("Error processing PDF:", error);
      alert(
        "Failed to process PDF file. Error: " + 
        (error.response?.data?.message || (error as any).message)
      );
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleExtractedDataChange = (index: number, field: keyof CreatePaymentRequest, value: any) => {
    const updatedData = [...pdfExtractedData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: field === "documentNumber" || field === "invoiceNo" ? value : value,
    };
    setPdfExtractedData(updatedData);
  };

  const addEmptyRow = () => {
    setPdfExtractedData([
      ...pdfExtractedData,
      {
        documentNumber: "",
        amount: 0,
        date: todayDate,
        netAmount: 0,
        invoiceNo: "",
      },
    ]);
  };

  const removeRow = (index: number) => {
    setPdfExtractedData(pdfExtractedData.filter((_, i) => i !== index));
  };

  const handleBulkSubmit = async () => {
    if (pdfExtractedData.length === 0) {
      alert("No data to submit");
      return;
    }

    setIsCreating(true);
    try {
      await axios.post(
        `${API_BASE_URL}account/${id}/payments/bulk-create`,
        { payments: pdfExtractedData },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSuccessMessage(`${pdfExtractedData.length} payments imported successfully`);
      setPdfExtractedData([]);
      setShowPdfPreview(false);
      await fetchAllPaymentsForSearch();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error: any) {
      console.error("Error importing payments:", error);
      alert(error.response?.data?.message || "Failed to import payments");
    } finally {
      setIsCreating(false);
    }
  };

  const filteredRecords = getPaginatedRecords(
    allPaymentRecords.filter(
      (payment) =>
        payment.documentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Get total filtered count for pagination
  const filteredTotalCount = allPaymentRecords.filter(
    (payment) =>
      payment.documentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase())
  ).length;

  return (
    <div className="w-full space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="text-green-600" size={20} />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Payments</h1>
          <p className="text-gray-600 text-sm mt-1">Manage payment records and import from PDF documents</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <button
            onClick={() => {
              setIsDrawerOpen(true);
              setEditingPaymentId(null);
              setFormData({
                documentNumber: "",
                amount: "",
                date: todayDate,
                netAmount: "",
                invoiceNo: "",
              });
            }}
            className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-4 py-2 rounded-lg font-semibold transition-all active:scale-95 flex-1 md:flex-none"
          >
            <Plus size={20} />
            New Payment
          </button>
        </div>
      </div>

      {/* Tabs for View/Upload */}
      <div className="space-y-4">
        {/* PDF Upload Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-4">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <Upload size={20} className="text-cyan-600" />
              Import from PDF
            </h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-cyan-500 transition-colors cursor-pointer relative">
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                disabled={uploadingPdf}
                className="w-full absolute inset-0 opacity-0 cursor-pointer"
              />
              {uploadingPdf ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="animate-spin text-cyan-600" size={32} />
                  <p className="text-gray-600">Processing PDF...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <FileText size={32} className="text-gray-400" />
                  <p className="font-semibold text-gray-700">Click to upload or drag and drop</p>
                  <p className="text-sm text-gray-500">PDF files only</p>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600">
              The PDF should contain columns: Document Number, Amount, Date, Net Amount, and Invoice No
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by document number or invoice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-cyan-50 to-teal-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Document Number</th>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Amount</th>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Net Amount</th>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Date</th>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Invoice No</th>
                <th className="px-6 py-4 text-left font-semibold text-gray-700">Created By</th>
                <th className="px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <Loader2 className="animate-spin mx-auto text-cyan-600 mb-2" size={32} />
                    <p className="text-gray-600">Loading payments...</p>
                  </td>
                </tr>
              ) : filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <p className="text-gray-500">No payments found</p>
                  </td>
                </tr>
              ) : (
                filteredRecords.map((payment) => (
                  <tr
                    key={payment.paymentId}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-800">{payment.documentNumber}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700">₹{payment.amount.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700">₹{payment.netAmount.toFixed(2)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700">{payment.date}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-700">{payment.invoiceNo}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 text-xs">{payment.createdByName || payment.createdBy}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleEdit(payment)}
                          className="p-2 hover:bg-blue-50 rounded-lg transition-colors text-blue-600 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() =>
                            setDeleteConfirm({
                              paymentId: payment.paymentId,
                              documentNumber: payment.documentNumber,
                            })
                          }
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredTotalCount > 10 && (
          <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 font-semibold"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Showing {Math.max(1, (page - 1) * 10 + 1)} to {Math.min(page * 10, filteredTotalCount)} of {filteredTotalCount}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= Math.ceil(filteredTotalCount / 10)}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 font-semibold"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Drawer */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-end md:justify-center">
          <div className="bg-white w-full md:w-96 rounded-t-2xl md:rounded-2xl shadow-2xl p-6 space-y-6 max-h-screen md:max-h-auto overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingPaymentId ? "Edit Payment" : "New Payment"}
              </h2>
              <button
                onClick={() => {
                  setIsDrawerOpen(false);
                  setEditingPaymentId(null);
                  setErrors({});
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="documentNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                  Document Number *
                </Label>
                <Input
                  id="documentNumber"
                  name="documentNumber"
                  value={formData.documentNumber}
                  onChange={handleInputChange}
                  placeholder="e.g., INV-001"
                  className={`${errors.documentNumber ? "border-red-500" : ""}`}
                />
                {errors.documentNumber && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={16} /> {errors.documentNumber}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="amount" className="block text-sm font-semibold text-gray-700 mb-2">
                  Amount *
                </Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`${errors.amount ? "border-red-500" : ""}`}
                />
                {errors.amount && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={16} /> {errors.amount}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="netAmount" className="block text-sm font-semibold text-gray-700 mb-2">
                  Net Amount *
                </Label>
                <Input
                  id="netAmount"
                  name="netAmount"
                  type="number"
                  step="0.01"
                  value={formData.netAmount}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  className={`${errors.netAmount ? "border-red-500" : ""}`}
                />
                {errors.netAmount && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={16} /> {errors.netAmount}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                  Date *
                </Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className={`${errors.date ? "border-red-500" : ""}`}
                />
                {errors.date && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={16} /> {errors.date}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="invoiceNo" className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice No (Narration) *
                </Label>
                <Input
                  id="invoiceNo"
                  name="invoiceNo"
                  value={formData.invoiceNo}
                  onChange={handleInputChange}
                  placeholder="e.g., INV2025001"
                  className={`${errors.invoiceNo ? "border-red-500" : ""}`}
                />
                {errors.invoiceNo && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle size={16} /> {errors.invoiceNo}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-2 rounded-lg font-semibold transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={18} />
                      Save
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsDrawerOpen(false);
                    setEditingPaymentId(null);
                    setErrors({});
                  }}
                  className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm space-y-6">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle size={32} />
              <div>
                <h2 className="text-xl font-bold">Delete Payment</h2>
                <p className="text-sm text-gray-600">Are you sure?</p>
              </div>
            </div>
            <p className="text-gray-700">
              This will permanently delete the payment{" "}
              <span className="font-semibold">{deleteConfirm.documentNumber}</span>.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-semibold transition-all active:scale-95"
              >
                Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview/Data Extraction Modal */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto space-y-6 p-6">
            <div className="flex justify-between items-center sticky top-0 bg-white pb-4 border-b">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Review Extracted Payment Data</h2>
                <p className="text-gray-600 text-sm mt-1">Review and edit the extracted data before importing</p>
              </div>
              <button
                onClick={() => {
                  setShowPdfPreview(false);
                  setPdfExtractedData([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Data Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Document Number</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Net Amount</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Invoice No</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfExtractedData.map((row, index) => (
                    <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.documentNumber}
                          onChange={(e) => handleExtractedDataChange(index, "documentNumber", e.target.value)}
                          placeholder="e.g., INV-001"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          value={row.amount}
                          onChange={(e) => handleExtractedDataChange(index, "amount", parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          step="0.01"
                          value={row.netAmount}
                          onChange={(e) => handleExtractedDataChange(index, "netAmount", parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="date"
                          value={row.date}
                          onChange={(e) => handleExtractedDataChange(index, "date", e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={row.invoiceNo}
                          onChange={(e) => handleExtractedDataChange(index, "invoiceNo", e.target.value)}
                          placeholder="e.g., INV2025001"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => removeRow(index)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600 hover:text-red-700"
                          title="Remove Row"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Add Row Button */}
            <button
              onClick={addEmptyRow}
              className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-cyan-500 text-gray-700 hover:text-cyan-600 font-semibold transition-colors"
            >
              + Add Row
            </button>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={handleBulkSubmit}
                disabled={isCreating || pdfExtractedData.length === 0}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-2 rounded-lg font-semibold transition-all active:scale-95 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    Import {pdfExtractedData.length > 0 && `(${pdfExtractedData.length})`}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowPdfPreview(false);
                  setPdfExtractedData([]);
                }}
                className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 rounded-lg font-semibold transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
